// src/components/games/dungeon/dungeonMap.ts

import { Character, GameState, Room } from './dungeonTypes';

export function displayMap(
  char: Character,
  dungeon: { [key: string]: Room },
  gs: GameState,
  print: (text: string) => void
): void {
  let currentFloor = "";
  const currentLocation = char.loc;

  if (["starting area", "center room", "east room", "trapdoor hold", "puzzle room"].includes(currentLocation)) {
    currentFloor = "main";
  } else if (["attic room", "roof", "closet", "observatory", "library"].includes(currentLocation)) {
    currentFloor = "upper";
  } else if (["basement", "crypt", "underground lake", "lake chamber"].includes(currentLocation)) {
    currentFloor = "lower";
  } else if (currentLocation === "secret") {
    currentFloor = "hidden";
  }

  print("You consult your hand-drawn map, focusing on this floor:");
  print("");

  const visited: { [key: string]: boolean } = {};
  for (const roomName in dungeon) {
    visited[roomName] = dungeon[roomName].v || false;
  }
  visited["starting area"] = true; // Always visible

  if (currentFloor === "main") {
    displayMainFloor(visited, dungeon, gs, print);
  } else if (currentFloor === "upper") {
    displayUpperFloor(visited, dungeon, print);
  } else if (currentFloor === "lower") {
    displayLowerFloor(visited, gs, print);
  } else if (currentFloor === "hidden") {
    displayHiddenArea(currentLocation, print);
  }

  print("");
  print("Current location: " + char.loc);
}

function displayMainFloor(
  visited: { [key: string]: boolean },
  dungeon: { [key: string]: Room },
  gs: GameState,
  print: (text: string) => void
): void {
  print("Main Floor:");
  print("");

  // Calculate position for center room
  let offset = 0;
  if (visited["puzzle room"] && dungeon["starting area"].e.west) offset += 20;
  if (visited["starting area"]) offset += 20;

  // Show upper connection
  if (visited["center room"] && visited["attic room"]) {
    print(" ".repeat(offset + 4) + "(to upper)");
    print(" ".repeat(offset + 4) + "    │");
  }

  // Build main floor rooms horizontally
  let line1 = "", line2 = "", line3 = "";

  if (visited["puzzle room"] && dungeon["starting area"].e.west) {
    line1 += "┌──────────────┐─────";
    line2 += "│ Puzzle Room  │─────";
    line3 += "└──────────────┘─────";
  }

  if (visited["starting area"]) {
    line1 += "┌──────────────┐";
    line2 += "│Starting Area │";
    line3 += "└──────────────┘";
    if (visited["center room"]) {
      line1 += "─────";
      line2 += "─────";
      line3 += "─────";
    }
  }

  if (visited["center room"]) {
    line1 += "┌──────────────┐";
    line2 += "│ Center Room  │";
    line3 += "└──────────────┘";
    if (visited["east room"]) {
      line1 += "─────";
      line2 += "─────";
      line3 += "─────";
    }
  }

  if (visited["east room"]) {
    line1 += "┌────────────┐";
    line2 += "│ East Room  │";
    line3 += "└────────────┘";
  }

  print(line1);
  print(line2);
  print(line3);

  // Trapdoor hold below center
  if (visited["center room"] && gs.trap && visited["trapdoor hold"]) {
    print(" ".repeat(offset + 7) + "│");
    print(" ".repeat(offset) + "┌──────────────────┐");
    print(" ".repeat(offset) + "│ Trapdoor Hold    │");
    print(" ".repeat(offset) + "└──────────────────┘");
  }
}

function displayUpperFloor(
  visited: { [key: string]: boolean },
  dungeon: { [key: string]: Room },
  print: (text: string) => void
): void {
  print("Upper Floor:");
  print("");

  // Observatory centered above attic
  if (visited["observatory"]) {
    print("               ┌──────────────┐");
    print("               │ Observatory  │");
    print("               └──────┬───────┘");
    print("                      │ (vertical)");
  }

  // Main horizontal row
  let line1 = "", line2 = "", line3 = "";

  if (visited["library"]) {
    line1 += "┌─────────┐";
    line2 += "│ Library │";
    line3 += "└─────┬───┘";
  } else {
    line1 += "           ";
    line2 += "           ";
    line3 += "           ";
  }

  // Connection from library to attic
  if (visited["library"] && visited["attic room"]) {
    line1 += "──────";
    line2 += "──────";
    line3 += "──────";
  } else if (visited["attic room"]) {
    line1 += "      ";
    line2 += "      ";
    line3 += "      ";
  }

  if (visited["attic room"]) {
    line1 += "┌─────────────┐";
    line2 += "│ Attic Room  │";
    line3 += "└─────┬───┬───┘";
  } else {
    line1 += "               ";
    line2 += "               ";
    line3 += "               ";
  }

  // Connection from attic to closet
  if (visited["attic room"] && visited["closet"]) {
    line1 += "──────";
    line2 += "──────";
    line3 += "──────";
  } else if (visited["closet"]) {
    line1 += "      ";
    line2 += "      ";
    line3 += "      ";
  }

  if (visited["closet"]) {
    line1 += "┌─────────┐";
    line2 += "│ Closet  │";
    if (visited["secret"] && dungeon["secret"].v) {
      line3 += "└────┬────┘";
    } else {
      line3 += "└─────────┘";
    }
  }

  print(line1);
  print(line2);
  print(line3);

  // Connections below main row
  let conn1 = "", conn2 = "", conn3 = "";

  // Library down to basement
  if (visited["library"]) {
    conn1 += "      │     ";
    conn2 += "      │     ";
    conn3 += " (to basement)";
  } else {
    conn1 += "            ";
    conn2 += "            ";
    conn3 += "            ";
  }

  // Space between library and attic connections
  conn1 += "   ";
  conn2 += "   ";
  conn3 += "   ";

  // Roof south of attic
  if (visited["roof"] && dungeon["attic room"].e.south) {
    conn1 += "  ┌──────┐  ";
    conn2 += "  │ Roof │  ";
    conn3 += "└──────┘ ";
  } else {
    conn1 += "                    ";
    conn2 += "                    ";
    conn3 += "                    ";
  }

  // Attic down to main floor
  if (visited["attic room"]) {
    conn1 += "│";
    conn2 += "│";
    conn3 += "(to main)";
  }

  // Space before closet connection
  if (visited["closet"]) {
    conn1 += "       ";
    conn2 += "";
    conn3 += "  ";
  }

  // Secret below closet
  if (visited["closet"] && visited["secret"] && dungeon["secret"].v) {
    conn1 += "        │";
    conn2 += "               │";
    conn3 += "  (to secret)";
  }

  if (conn1.trim()) print(conn1);
  if (conn2.trim()) print(conn2);
  if (conn3.trim()) print(conn3);
}

function displayLowerFloor(
  visited: { [key: string]: boolean },
  gs: GameState,
  print: (text: string) => void
): void {
  print("Lower Floor:");
  print("");

  // Calculate offset for "to upper" indicator to align with basement
  let offset = 0;
  if (visited["underground lake"]) offset += 25;
  if (visited["crypt"]) offset += 14;

  // Up to library - aligned with basement
  if (visited["basement"]) {
    const upperLabel = " ".repeat(offset + 1) + "(to upper)";
    const upperLine = " ".repeat(offset + 5) + "│";
    print(upperLabel);
    print(upperLine);
  }

  // Main horizontal row
  let line1 = "", line2 = "", line3 = "";

  if (visited["underground lake"]) line1 += "┌──────────────────┐─────";
  if (visited["crypt"]) line1 += "┌───────┐─────";
  if (visited["basement"]) line1 += "┌──────────┐";

  if (visited["underground lake"]) line2 += "│ Underground Lake │─────";
  if (visited["crypt"]) line2 += "│ Crypt │─────";
  if (visited["basement"]) line2 += "│ Basement │";

  if (visited["underground lake"]) line3 += "└─────────┬────────┘─────";
  if (visited["crypt"]) line3 += "└───────┘─────";
  if (visited["basement"]) line3 += "└──────────┘";

  print(line1);
  print(line2);
  print(line3);

  if (gs.lake && visited["lake chamber"]) {
    print("          │ (vertical)");
    print("   ┌──────────────┐");
    print("   │ Lake Chamber │");
    print("   └──────────────┘");
  }
}


function displayHiddenArea(currentLocation: string, print: (text: string) => void): void {
  if (currentLocation === "secret") {
    print("Hidden Area (Secret):");
    print("");
    print("  (to closet)");
    print("       │");
    print("┌──────────────┐");
    print("│ Secret Room  │");
    print("└──────────────┘");
  } else if (currentLocation === "puzzle room") {
    print("Hidden Area (Puzzle):");
    print("");
    print("  (to starting area)");
    print("       │");
    print("┌──────────────┐");
    print("│ Puzzle Room  │");
    print("└──────────────┘");
  }
}